export namespace AppUtils {
  export const formatCompaniesToSelectOptions = (masters: Array<{ id: number; name: string }>) =>
    masters.map((item) => ({
      label: item.name,
      id: item.id,
      value: item.name,
    }));

  export const formatCupsToSelectOptions = (
    cups: Array<{ id: number; identifier: string; alias: string; companyId: number }>
  ) =>
    cups.map((cup) => ({
      label: cup.alias,
      id: cup.id,
      value: String(cup.id),
      identifier: cup.identifier,
      companyId: cup.companyId,
    }));

  export const filterCupsByCompanies = (
    cups: Array<{ id: number; identifier: string; companyId: number; alias: string }>,
    companies: Array<{ id: number; label: string; value: string }>
  ) => {
    const selectedCompanyIds = companies.map((c) => c.id);
    const filteredCups = cups.filter((cup) => selectedCompanyIds.includes(cup.companyId));

    const aliasCount = new Map<string, number>();
    filteredCups.forEach((cup) => {
      aliasCount.set(cup.alias, (aliasCount.get(cup.alias) || 0) + 1);
    });

    const companyNameMap = new Map<number, string>();
    companies.forEach((c) => companyNameMap.set(c.id, c.label));

    return filteredCups.map((cup) => {
      // If the same alias appears multiple times, add company name to disambiguate
      const isDuplicate = (aliasCount.get(cup.alias) || 0) > 1;
      const companyName = companyNameMap.get(cup.companyId) || '';
      const label = isDuplicate ? `${cup.alias} (${companyName})` : cup.alias;

      return {
        label,
        id: cup.id,
        value: String(cup.id),
        identifier: cup.identifier,
        companyId: cup.companyId,
      };
    });
  };

  // CUPS identifiers that bypass standard validation rules
  const ALLOWED_SPECIAL_CUPS = ['F02.1', 'F02.2', 'K02031A1'];

  export const validateCupsStructure = (cups: string): { isValid: boolean; error?: string } => {
    if (!cups || cups.trim() === '') {
      return { isValid: false, error: 'CUPS is required' };
    }

    // Allow special CUPS that don't follow standard format
    if (ALLOWED_SPECIAL_CUPS.includes(cups.trim())) {
      return { isValid: true };
    }

    if (cups.length !== 20 && cups.length !== 22) {
      return { isValid: false, error: 'Invalid CUPS format' };
    }

    if (!cups.startsWith('ES')) {
      return { isValid: false, error: 'Invalid CUPS format' };
    }

    const lastTwoChars = cups.slice(-2);
    if (!/^[A-Z]{2}$/.test(lastTwoChars)) {
      return { isValid: false, error: 'Invalid CUPS format' };
    }

    return { isValid: true };
  };

  /**
   * Gets the active company from a user (first company in the array)
   * @param user User object with companies array
   * @returns The first company or undefined if not found
   */
  export const getActiveCompany = <T extends { companies: Array<unknown> }>(
    user: T | null
  ): T['companies'][number] | undefined => {
    if (!user || !user.companies || user.companies.length === 0) return undefined;
    return user.companies[0];
  };
}
